import { createContext, useContext, useState, useEffect } from "react";

// Theme and language context
const ThemeContext = createContext();

// Languages available in the app
export const languages = {
  en: { name: "English", code: "en", label: "English" },
  pt: { name: "Portuguese", code: "pt", label: "Portuguese" },
  ur: { name: "Urdu", code: "ur", label: "Urdu" },
};

export function ThemeProvider({ children }) {
  // Initialize theme from localStorage only (never from OS/system preference)
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "light";
  });

  // Apply theme to document
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Language state. LanguageContext is the real owner of the "language" key;
  // this copy is only still here because it is part of the useTheme() value.
  // It deliberately does NOT write the key on mount: this provider sits outside
  // LanguageProvider, so its effects run last and a stale value here would
  // overwrite whatever LanguageProvider just resolved (which is how /br used to
  // end up saved as English). Writes happen only through changeLanguage.
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem("language");
    return savedLanguage && languages[savedLanguage] ? savedLanguage : "en";
  });

  const changeLanguage = (lang) => {
    if (languages[lang]) {
      setLanguage(lang);
      localStorage.setItem("language", lang);
    }
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, language, changeLanguage, languages }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
